import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import BaseModal from '../../../../components/BaseComponents/BaseModal/BaseModal';
import { useToast } from '../../../../context/ToastContext';
import { grantReleaseException, errorMessage } from '../../../../api/document/documentCentreApi';
import { formatMoney } from '../documentCentre.constants';
import s from './DispatchReviewModal.module.css';

const TYPE_LABEL = {
  flight_voucher: 'Flight documents',
  hotel_voucher: 'Hotel vouchers',
  transfer_voucher: 'Transfer vouchers',
  car_rental_voucher: 'Car rental vouchers',
  visa_voucher: 'Visa documents',
  cruise_voucher: 'Cruise vouchers',
};

/**
 * Authorised financial exception (spec §8). Releases travel documents despite
 * an outstanding balance; every other check still applies. The staff member,
 * date and mandatory reason are recorded.
 */
const ReleaseExceptionModal = ({ open, bookingReference, outstanding, subtypes = [], onClose, onGranted }) => {
  const { showToast } = useToast();
  const [scope, setScope] = useState('all');
  const [picked, setPicked] = useState(new Set(subtypes));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const valid = reason.trim().length >= 5 && (scope === 'all' || picked.size > 0);

  const toggle = (subtype) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(subtype)) next.delete(subtype);
      else next.add(subtype);
      return next;
    });

  const submit = async () => {
    setSaving(true);
    try {
      await grantReleaseException({
        bookingReference,
        reason: reason.trim(),
        documentSubtypes: scope === 'all' ? [] : [...picked],
      });
      showToast(`Financial exception recorded for ${bookingReference}`, 'success');
      onGranted?.();
      onClose?.();
    } catch (err) {
      showToast(await errorMessage(err, 'Could not record the exception'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={saving ? undefined : onClose}
      title="Financial exception"
      width="560px"
      footer={
        <div className={s.footer}>
          <span className={s.footerNote}>Recorded with your name, the date and the reason.</span>
          <div className={s.footerButtons}>
            <button type="button" className={s.secondaryBtn} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className={s.primaryBtn} onClick={submit} disabled={!valid || saving}>
              <ShieldCheck size={14} /> {saving ? 'Saving...' : 'Release despite balance'}
            </button>
          </div>
        </div>
      }
    >
      <p className={s.hint} style={{ marginTop: 0 }}>
        Booking <strong>{bookingReference}</strong> has an outstanding balance of <strong>{formatMoney(outstanding)}</strong>.
        This releases its travel documents for customer use anyway. An unconfirmed or cancelled service, a missing ticket
        or missing information still blocks. If the balance grows or a payment is reversed, the exception stops applying.
      </p>
      <div className={s.options}>
        <span className={s.optionsTitle}>Applies to</span>
        <label className={s.check}>
          <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} />
          All travel documents of this booking
        </label>
        <label className={s.check}>
          <input type="radio" checked={scope === 'some'} onChange={() => setScope('some')} />
          Only these types
        </label>
        {scope === 'some' && (
          <div className={s.optionRow}>
            {Object.entries(TYPE_LABEL).map(([subtype, label]) => (
              <label key={subtype} className={s.check}>
                <input type="checkbox" checked={picked.has(subtype)} onChange={() => toggle(subtype)} />
                {label}
              </label>
            ))}
          </div>
        )}
        <span className={s.optionsTitle}>Reason (mandatory)</span>
        <textarea
          className={s.textarea}
          value={reason}
          maxLength={2000}
          placeholder="Why the documents may be released before payment"
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </BaseModal>
  );
};

export default ReleaseExceptionModal;
